import { useGeo } from "@sushicorp/contexts";
import { useCountries } from "@sushicorp/contexts";
import { useFetchCoordsFromGoogleAddress } from "@sushicorp/services";
import { useFetchAutocompletePrediction } from "@sushicorp/services";
import { dropdownConfigs } from "@sushicorp/utils";
import { debounce } from "@sushicorp/utils";
import { events } from "artisn/analytics";
import { SearchBar } from "artisn-ui-react";
import React, { useEffect, useState } from "react";

import Styles, { SearchAddressContentStyled } from "./SearchAddress.styles";
import { SearchAddressProps as Props } from "./SearchAddress.types";
import InfoCard from "../InfoCard/InfoCard";
import useAnalytics from "contexts/analytics/analytics.context.hooks";
import useI18n from "hooks/useI18n";
import { Google } from "types/geo.types";
import { dismissErrorNotification } from "utils/notifications.utils";
import { createErrorNotification } from "utils/notifications.utils";

import LoadingSVG from "../../../../public/assets/images/loading.svg";

const { logFindLocation } = events.geo;

const SearchAddress: React.FC<Props> = props => {
  const t = useI18n();
  const { onPredictedPlaces, onContentClick } = props;
  const { showSearchDropdown = true, onChange } = props;
  const { onInputChange } = props;
  const { selectedCountry } = useCountries();
  const { lat, lng } = selectedCountry;
  const [query, setQuery] = useState<string | undefined>();
  const { selectedCoordinates } = useGeo();
  const { analyticsInitialized } = useAnalytics();
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPlaceName, setSelectedPlaceName] = useState("");
  const {
    data: predictedPlaces,
    isLoading: predictedPlacesLoading,
    error: predictedPlacesError
  } = useFetchAutocompletePrediction(
    query,
    selectedCoordinates ?? { lat, lng }
  );
  const { data: place, error: placeError } = useFetchCoordsFromGoogleAddress(
    process.env.NEXT_PUBLIC_MAPS_API_KEY ?? "",
    selectedPlaceName
  );
  const submitHandler = (query: string) => {
    setQuery(query);
  };

  const changeHandler = (item: Google.Autocomplete) => {
    const { description } = item;

    setSelectedPlaceName(description);
    setShowDropdown(false);
  };

  const onInputClick = () => {
    setShowDropdown(true);
  };

  const clearHandler = () => {
    setQuery(undefined);
    setShowDropdown(false);
  };

  const queryChangeHandler = debounce((value: string) => {
    if (value.length < 3) return;
    setQuery(value);
    onInputChange?.(value);
  }, 500);

  useEffect(() => {
    if (!place || !analyticsInitialized) return;
    logFindLocation();
    onChange?.(place);
  }, [analyticsInitialized, onChange, place]);

  useEffect(() => {
    if (!predictedPlacesError) return;
    dismissErrorNotification();
    createErrorNotification(t.errors.failSearch);
    console.error(predictedPlacesError);
  }, [predictedPlacesError, t]);

  useEffect(() => {
    if (!placeError) return;
    dismissErrorNotification();
    createErrorNotification(t.errors.fetchAddress);
    console.error(placeError);
  }, [placeError, t]);

  useEffect(() => {
    if (!predictedPlaces) return;
    onPredictedPlaces?.(predictedPlaces);
  }, [predictedPlaces, onPredictedPlaces]);

  return (
    <Styles className="SearchAddress">
      <SearchBar
        className="SearchAddress__search-bar field"
        placeholder={t.profile.address.placeHolderSearch}
        onSubmit={submitHandler}
        onInputFocus={() => setSelectedPlaceName("")}
        onInputClick={onInputClick}
        dropdownConfigs={{ ...dropdownConfigs, showDropdown }}
        onClear={clearHandler}
        onInputBlur={() => setShowDropdown(false)}
        onChange={queryChangeHandler}
        SearchIcon={
          predictedPlacesLoading ? (
            <LoadingSVG
              className="SearchAddress__loading"
              viewBox="0 0 66 66"
            />
          ) : null
        }
      >
        {showSearchDropdown ? (
          <SearchAddressContentStyled className="SearchAddressContent">
            {predictedPlaces?.map((item, index) => {
              const { description } = item;
              return (
                <InfoCard
                  className="SearchAddressContent__address-info"
                  key={index}
                  value={description}
                  onContentClick={() => {
                    changeHandler(item);
                    onContentClick?.(item);
                  }}
                />
              );
            })}
            {predictedPlaces?.length === 0 ? (
              <p className="SearchAddressContent__empty">
                No se encontraron resultados
              </p>
            ) : null}
          </SearchAddressContentStyled>
        ) : null}
      </SearchBar>
    </Styles>
  );
};

SearchAddress.defaultProps = {};

export default SearchAddress;
